import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Select, MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
    IconButton, Chip, Typography, Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
    adminGetQuestions, adminCreateQuestion, adminUpdateQuestion, adminDeleteQuestion,
} from '../../api/quizApi';

const CATEGORIES = ['RSC', 'Product', 'Residential'];
const CATEGORY_COLORS = { RSC: '#1A8CFF', Product: '#F59E0B', Residential: '#10B981' };
const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

const EMPTY_FORM = {
    category: 'RSC',
    questionText: '', questionTextEn: '', questionTextMy: '',
    optionA: '', optionAEn: '', optionAMy: '',
    optionB: '', optionBEn: '', optionBMy: '',
    optionC: '', optionCEn: '', optionCMy: '',
    optionD: '', optionDEn: '', optionDMy: '',
    correctOption: 'A',
    explanation: '',
    isActive: true,
    sortOrder: 0,
};

const QuizQuestionsAdmin = () => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const fetchQuestions = async () => {
        setLoading(true);
        try {
            const res = await adminGetQuestions();
            const list = (res.data || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
            setQuestions(list);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchQuestions(); }, []);

    const handleOpen = (q = null) => {
        if (q) {
            setEditingQuestion(q);
            setForm({
                category: q.category || 'RSC',
                questionText: q.questionText || '', questionTextEn: q.questionTextEn || '', questionTextMy: q.questionTextMy || '',
                optionA: q.optionA || '', optionAEn: q.optionAEn || '', optionAMy: q.optionAMy || '',
                optionB: q.optionB || '', optionBEn: q.optionBEn || '', optionBMy: q.optionBMy || '',
                optionC: q.optionC || '', optionCEn: q.optionCEn || '', optionCMy: q.optionCMy || '',
                optionD: q.optionD || '', optionDEn: q.optionDEn || '', optionDMy: q.optionDMy || '',
                correctOption: q.correctOption || 'A',
                explanation: q.explanation || '',
                isActive: q.isActive !== false,
                sortOrder: q.sortOrder ?? 0,
            });
        } else {
            setEditingQuestion(null);
            setForm(EMPTY_FORM);
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.questionText.trim() && !form.questionTextEn.trim()) {
            return alert('Question text (Chinese or English) is required');
        }
        setSaving(true);
        try {
            const payload = { ...form, sortOrder: Number(form.sortOrder) || 0 };
            if (editingQuestion) {
                await adminUpdateQuestion({ ...payload, quizQuestionId: editingQuestion.quizQuestionId });
            } else {
                await adminCreateQuestion(payload);
            }
            setDialogOpen(false);
            fetchQuestions();
        } catch (err) {
            console.error(err);
            alert('Error saving question');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (quizQuestionId) => {
        if (!window.confirm('Delete this question?')) return;
        try {
            await adminDeleteQuestion(quizQuestionId);
            fetchQuestions();
        } catch (err) {
            console.error(err);
            alert('Error deleting question');
        }
    };

    const optionField = (letter) => (
        <div key={letter} style={{ display: 'flex', gap: 12 }}>
            <TextField
                label={`Option ${letter} (Chinese)`}
                value={form[`option${letter}`]}
                onChange={e => setForm(f => ({ ...f, [`option${letter}`]: e.target.value }))}
                size="small"
                sx={{ flex: 1 }}
            />
            <TextField
                label={`Option ${letter} (English)`}
                value={form[`option${letter}En`]}
                onChange={e => setForm(f => ({ ...f, [`option${letter}En`]: e.target.value }))}
                size="small"
                sx={{ flex: 1 }}
            />
        </div>
    );

    return (
        <div style={{ padding: '24px 32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <Typography variant="h5" fontWeight={700}>Quiz Questions</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ textTransform: 'none' }}>
                    Add Question
                </Button>
            </div>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Question</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Correct</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Active</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {questions.map((q, idx) => (
                            <TableRow key={q.quizQuestionId} hover>
                                <TableCell sx={{ fontSize: 12 }}>{q.sortOrder ?? idx + 1}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={q.category}
                                        size="small"
                                        sx={{ fontSize: 10, bgcolor: CATEGORY_COLORS[q.category] || '#94A3B8', color: 'white' }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontSize: 12, maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {q.questionTextEn || q.questionText}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>{q.correctOption}</TableCell>
                                <TableCell>
                                    {q.isActive
                                        ? <Chip label="Active" size="small" sx={{ fontSize: 10, bgcolor: '#10B981', color: 'white' }} />
                                        : <Chip label="Inactive" size="small" sx={{ fontSize: 10, bgcolor: '#94A3B8', color: 'white' }} />}
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small" onClick={() => handleOpen(q)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => handleDelete(q.quizQuestionId)}><DeleteIcon fontSize="small" color="error" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && questions.length === 0 && (
                            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94A3B8' }}>No questions yet</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <FormControl size="small" sx={{ maxWidth: 240 }}>
                        <InputLabel>Category</InputLabel>
                        <Select value={form.category} label="Category" onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <TextField label="Question (Chinese)" value={form.questionText} onChange={e => setForm(f => ({ ...f, questionText: e.target.value }))} size="small" multiline rows={2} />
                    <TextField label="Question (English)" value={form.questionTextEn} onChange={e => setForm(f => ({ ...f, questionTextEn: e.target.value }))} size="small" multiline rows={2} />

                    <Divider sx={{ my: 0.5 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94A3B8' }}>Options</Typography>
                    {OPTION_LETTERS.map(optionField)}

                    <div style={{ display: 'flex', gap: 12 }}>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Correct Option</InputLabel>
                            <Select value={form.correctOption} label="Correct Option" onChange={e => setForm(f => ({ ...f, correctOption: e.target.value }))}>
                                {OPTION_LETTERS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Sort Order"
                            type="number"
                            value={form.sortOrder}
                            onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))}
                            size="small"
                            sx={{ flex: 1 }}
                        />
                    </div>

                    <TextField label="Explanation" value={form.explanation} onChange={e => setForm(f => ({ ...f, explanation: e.target.value }))} size="small" multiline rows={2} />

                    <FormControlLabel control={<Switch checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />} label="Active" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>{editingQuestion ? 'Update' : 'Create'}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default QuizQuestionsAdmin;
